/**
 * OutstandingSummary.tsx  —  FIXED
 *
 * Fix: `Grid` component does not have a `divider` prop in MUI v5.
 *   Old:  <Grid container divider={<Divider orientation="vertical" flexItem />}>
 *   Error: Property 'divider' does not exist on type Grid props
 *
 *   Fix: Remove `divider` prop. Use `borderRight` sx on each Grid item
 *        to draw the vertical separator lines manually.
 *        On mobile (xs) the border becomes a bottom border via responsive sx.
 *
 * Drop this component into ViewLedger.tsx replacing the old OutstandingSummary.
 */

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingDownIcon   from "@mui/icons-material/TrendingDown";
import TrendingUpIcon     from "@mui/icons-material/TrendingUp";
import { IOutstanding }  from "../Interfaces/ILedger";

const formatCurrency = (amount: number) =>
  `₹${Number(amount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const OutstandingSummary: React.FC<{
  outstanding:  IOutstanding;
  customerName: string;
  isMobile:     boolean;
}> = ({ outstanding, customerName, isMobile }) => {
  const isCredit  = outstanding.balance < 0;
  const isCleared = outstanding.balance === 0;

  const stats = [
    {
      label: "Total Invoiced",
      value: formatCurrency(outstanding.totalDebit),
      color: "primary.main",
      icon:  <TrendingUpIcon fontSize="small" color="primary" />,
    },
    {
      label: "Total Received",
      value: formatCurrency(outstanding.totalCredit),
      color: "success.main",
      icon:  <TrendingDownIcon fontSize="small" color="success" />,
    },
    {
      label: isCredit ? "Advance / Overpaid" : "Outstanding Due",
      value: formatCurrency(Math.abs(outstanding.balance)),
      color: isCredit
        ? "success.main"
        : isCleared
          ? "text.secondary"
          : "error.main",
      icon: (
        <AccountBalanceIcon
          fontSize="small"
          color={isCredit ? "success" : isCleared ? "disabled" : "error"}
        />
      ),
    },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: "hidden" }}>

      {/* Coloured banner */}
      <Box
        sx={{
          px: 2, py: 1,
          bgcolor: isCredit
            ? "success.main"
            : isCleared
              ? "grey.500"
              : "error.main",
          color: "white",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            Account Summary — {customerName}
          </Typography>
          <Chip
            label={
              isCredit  ? "Advance Paid"   :
              isCleared ? "Fully Cleared"  :
                          "Amount Due"
            }
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.25)",
              color:   "white",
              fontWeight: 600,
            }}
          />
        </Stack>
      </Box>

      {/* ── FIX: Grid without divider prop — use borderRight on items instead ── */}
      <Grid container>
        {stats.map((stat, idx) => (
          <Grid
            key={stat.label}
            item
            xs={12}
            sm={4}
            sx={{
              // Vertical separator between columns on sm+ screens
              borderRight: {
                xs: "none",
                sm: idx < stats.length - 1 ? "1px solid" : "none",
              },
              borderBottom: {
                xs: idx < stats.length - 1 ? "1px solid" : "none",
                sm: "none",
              },
              borderColor: "divider",
            }}
          >
            <Box sx={{ p: isMobile ? 1.5 : 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                {stat.icon}
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
              </Stack>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                fontWeight={700}
                color={stat.color}
              >
                {stat.value}
              </Typography>
              {/* "Fully Paid" badge on the balance cell */}
              {idx === 2 && isCleared && (
                <Chip
                  label="Fully Cleared"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ mt: 0.5, fontSize: 11 }}
                />
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default OutstandingSummary;